import React, { createContext, useContext } from 'react';
import CryptoJS from 'crypto-js';

interface EncryptionContextType {
  encrypt: (text: string) => Promise<string>;
  decrypt: (encryptedText: string) => Promise<string>;
}

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (!context) {
    throw new Error('useEncryption must be used within an EncryptionProvider');
  }
  return context;
};

export const EncryptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const encrypt = async (text: string): Promise<string> => {
    // Get encryption key from secure storage
    const encryptionKey = localStorage.getItem('masterKey');
    if (!encryptionKey) {
      throw new Error('Encryption key not found');
    }

    // Encrypt the text directly without JSON stringification
    const encrypted = CryptoJS.AES.encrypt(text, encryptionKey).toString();
    return encrypted;
  };

  const decrypt = async (encryptedText: string): Promise<string> => {
    if (!encryptedText) return '';
    
    // Get encryption key from secure storage
    const encryptionKey = localStorage.getItem('masterKey');
    if (!encryptionKey) {
      throw new Error('Encryption key not found');
    }

    // Decrypt the text and parse JSON
    const decrypted = CryptoJS.AES.decrypt(encryptedText, encryptionKey);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    try {
      return JSON.parse(decryptedString);
    } catch (e) {
      // Fallback for legacy data that wasn't JSON stringified
      return decryptedString;
    }
  };

  const value = {
    encrypt,
    decrypt
  };

  return (
    <EncryptionContext.Provider value={value}>
      {children}
    </EncryptionContext.Provider>
  );
}; 